import { Flex, Text, VStack } from '@chakra-ui/react'
import IconEditPencil from '~/assets/icons/icon-edit-pencil.svg?react'
import IconPlus from '~/assets/svgs/icon-plus.svg?react'
import IconTrash from '~/assets/svgs/icon-trash.svg?react'
import { useModalState } from '~/atoms/ModalState'
import { useSettingsState } from '~/atoms/SettingsState'
import { useUserState } from '~/atoms/UserState'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import { QuickActionButton } from '~/components/shared/QuickActionButton'
import { TextButton } from '~/components/shared/TextButton'
import { CustomStatusModal } from '~/pages/Home/Modals/CustomStatusModal/CustomStatusModal'
import {
  useDeleteCustomStatus,
  useGetCustomStatuses,
} from '~/queries/customStatusQueries'
import { useQueueLogout } from '~/queries/queueQueries'
import { Availability } from '~/types/AgentCustomStatus'
import { Size } from '~/types/ThemeTypes'
import { isUserAdmin } from '~/utils/auth'
import { useCustomMultiStyleConfig } from '~/utils/chakraUtils'
import { showSuccessToast } from '~/utils/toast'

import { settingsColorKeys } from './componentStyles/_colors'
import { SettingToggleDependentSection } from './SettingItems'
import { DefaultSettingsWrapper, SubheadingSection } from './Shared'

interface CallCenterSettingsProps {
  pbxId: number | undefined
  extensionId: number | undefined
}
export const CallCenterSettings = ({
  pbxId,
  extensionId,
}: CallCenterSettingsProps) => {
  const {
    settings: {
      callCenter: { isEnabled: isCallCenterModeEnabled },
    },
    setCallCenterAgentEnabled,
    setDoNotDisturb,
  } = useSettingsState(pbxId)
  const { userRoles } = useUserState()

  const queueLogout = useQueueLogout({ extensionId })

  const { data: customStatuses, refetch: refetchStatuses } =
    useGetCustomStatuses(pbxId)
  const { mutate: deleteCustomStatus } = useDeleteCustomStatus(() => {
    showSuccessToast({ message: 'Status deleted successfully' })
  })

  const handleCallCenterModeToggle = () => {
    const newCallCenterMode = !isCallCenterModeEnabled
    if (newCallCenterMode === false) {
      queueLogout()
    } else {
      setDoNotDisturb(undefined)
    }
    setCallCenterAgentEnabled(newCallCenterMode)
  }

  const {
    customStatusModal: [, setCustomStatusModalState],
    confirmModal: [, setConfirmModalState],
  } = useModalState()

  const handleEditStatus = (status: {
    id: number
    name: string
    description: string
    availability: Availability
  }) => {
    setCustomStatusModalState({
      open: true,
      customStatus: {
        id: status.id,
        name: status.name,
        description: status.description,
        availability: status.availability,
      },
    })
  }

  const handleNewStatus = () => {
    setCustomStatusModalState({
      open: true,
    })
  }

  const styles = useCustomMultiStyleConfig('CallCenterSettingsStyle')

  const Colors = settingsColorKeys.Settings.CallCenterSettings

  return (
    <>
      <DefaultSettingsWrapper>
        <SettingToggleDependentSection
          label="Enable Agent Mode"
          isChecked={isCallCenterModeEnabled}
          onClick={handleCallCenterModeToggle}
          toggleOnContent={
            <VStack w="full" gap="1rem">
              {isUserAdmin(userRoles) && (
                <SubheadingSection>
                  <Flex sx={styles.customStatusHeaderContainer}>
                    <Flex sx={styles.customStatusHeaderContent}>
                      <Text textStyle="Body2_Bold">Custom Agent Statuses</Text>
                      <Text textStyle="Body2">
                        Set up custom statuses to track activities outside the
                        call queue, like lunches, breaks, meetings, and more.
                        Keep your team’s activity organized and visible.
                      </Text>
                    </Flex>
                    <Flex sx={styles.customStatusWrapper}>
                      {customStatuses?.map((status) => {
                        return (
                          <Flex
                            sx={styles.customStatusContainer}
                            key={status.id}
                          >
                            <Flex sx={styles.customStatusContent}>
                              <Flex
                                sx={{ alignItems: 'center', gap: '0.25rem' }}
                              >
                                <Flex
                                  sx={{
                                    ...styles.statusIndicator,
                                    background:
                                      status.availability === 'Away'
                                        ? Colors.CustomStatus.StatusIndicator
                                            .Away
                                        : Colors.CustomStatus.StatusIndicator
                                            .Online,
                                  }}
                                />
                                <Text sx={styles.statusTitle}>
                                  {status.name}
                                </Text>
                              </Flex>
                              <Text sx={styles.statusDescription}>
                                {status.description}
                              </Text>
                            </Flex>
                            <Flex sx={styles.actionButtons}>
                              <QuickActionButton
                                variant={QuickActionButtonVariants.Default_100}
                                actionIcon={IconEditPencil}
                                tooltipText="Edit Status"
                                size={Size.sm}
                                onClick={() => handleEditStatus(status)}
                              />
                              <QuickActionButton
                                variant={
                                  QuickActionButtonVariants.Destructive_100
                                }
                                actionIcon={IconTrash}
                                tooltipText="Delete Status"
                                size={Size.sm}
                                iconProps={{
                                  color: 'gray.1300',
                                  _groupHover: { color: 'red.800' },
                                }}
                                onClick={() => {
                                  setConfirmModalState({
                                    title: 'Delete Custom Status',
                                    message: (
                                      <Text>
                                        Your custom status,{' '}
                                        <Text as="span" fontWeight="bold">
                                          {status.name}
                                        </Text>
                                        , will be permanently deleted. Are you
                                        sure?
                                      </Text>
                                    ),
                                    confirmText: 'Delete Status',
                                    cancelText: 'Cancel',
                                    closeAfterConfirm: true,
                                    isDestructive: true,
                                    onConfirm: () => {
                                      deleteCustomStatus(status.id)
                                    },
                                  })
                                }}
                              />
                            </Flex>
                          </Flex>
                        )
                      })}
                      <TextButton
                        leftIcon={IconPlus}
                        onLinkClick={handleNewStatus}
                        leftIconProps={{
                          sx: {
                            boxSize: '12px',
                            position: 'relative',
                            bottom: '.5px',
                            stroke: 'inherit',
                            '& path': { strokeWidth: '5px' },
                          },
                        }}
                      >
                        New Status
                      </TextButton>
                    </Flex>
                  </Flex>
                </SubheadingSection>
              )}
            </VStack>
          }
        />
      </DefaultSettingsWrapper>
      <CustomStatusModal onCreated={refetchStatuses} />
    </>
  )
}
